import SellerIcon from 'assets/svg/seller-icon/seller-icon.svg'
import SellerIllustration from 'assets/svg/seller-icon/seller-illustration-lg.svg'
import Button from 'common/components/Button'
import Flex from 'common/components/Flex'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import {
  ACTIVE_THEME_NAME,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import redirect from 'lib/common/redirect'
import { getCookie } from 'lib/cookie'
import eventTracking from 'lib/event-tracking'
import trackPosthog from 'lib/posthog/tracker'
import ThemeColorHelper from 'lib/theme-color-helper'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ModalOtaInvalid from 'services/Auth/widgets/ModalOtaInvalid'
import IdentityVerificationModal from 'services/Home/widgets/IdentityVerificationModal'
import ModalChangeToBusiness from 'services/Profile/components/ModalChangeToBusiness'
import ModalNIB from 'services/Seller/components/ModalNIB'
import ModalRalaliVsConnect from 'services/Seller/components/ModalRalaliVsConnect'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

interface StartSellingButtonProps {
  buttonText?: string
  buttonIcon?: JSX.Element | null
  buttonColor?: string
  buttonVariant?: 'contained' | 'outlined'
  onHoverColor?: string
  version?: 'v1' | 'v2'
}

const StyledButton = styled(Button)`
  color: ${({ color }) => color};
  &:hover {
    color: ${({ onHoverColor }) =>
      ThemeColorHelper(onHoverColor) || 'black'};
  }
`

const StoreNameWrapper = styled(Typography)`
  width: 144px;
  :hover {
    color: ${ThemeColorHelper('orange')};
    cursor: pointer;
  }
  color: ${() =>
    ACTIVE_THEME_NAME === 'default'
      ? 'inherit'
      : theme.colors.custom.white};
  svg {
    flex-shrink: 0;
  }
`

const StyledSellerIcon = styled(SellerIcon)`
  color: ${ThemeColorHelper('orange')};
`

const StoreName = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`

function StartSellingButton({
  buttonText = 'Mulai Berjualan',
  buttonIcon = <StyledSellerIcon />,
  buttonColor = theme.colors.orange,
  buttonVariant = 'outlined',
  onHoverColor = 'black',
  version = 'v1',
}: StartSellingButtonProps): JSX.Element {
  // LOCAL STATE
  const [
    isOpenModalChangeToBusiness,
    setIsOpenModalChangeToBusiness,
  ] = useState(false)
  const [isOpenModalNIB, setIsOpenModalNIB] = useState(false)
  const [
    isOpenVerificationModal,
    setIsOpenVerificationModal,
  ] = useState(false)
  const [
    isOpenErrorVerifyModal,
    setIsOpenErrorVerifyModal,
  ] = useState(false)
  const [isOpenModalComparison, setIsOpenModalComparison] = useState(
    false,
  )
  const [storeData, setStoreData] = useState<Record<
    string,
    any
  > | null>(null)

  // GLOBAL STATE
  const { errorMessageOtp } = useStoreState((state) => state.otp)
  const { profileData } = useStoreState((state) => state.profile)
  const {
    data: profilingData,
    isLoading: isLoadingProfiling,
  } = useStoreState((state) => state.profiling)
  const {
    isLoading: isLoadingStore,
    isGetStoreListCalled,
    firstStore,
  } = useStoreState((state) => state.storeSeller)
  const router = useRouter()

  //   ACTIONS
  const { getProfiling } = useStoreActions(
    (actions) => actions.profiling,
  )
  const { getStoreList } = useStoreActions(
    (actions) => actions.storeSeller,
  )
  const { getUserProfile } = useStoreActions(
    (actions) => actions.profile,
  )

  //   DERIVED STATE
  const isLoading = isLoadingProfiling || isLoadingStore
  const isOnboardingPage = router.pathname === '/seller/onboarding'
  const isLogin =
    !!getCookie('access_token') || !!getCookie('refresh_token')
  const isVerified =
    profileData?.is_email_verified &&
    profileData?.is_handphone_verified
  const bussinesGroup = useMemo(() => {
    return profilingData.find(
      (item) => item.group_code === 'business-type',
    )
  }, [profilingData])
  const bussinessType = useMemo(
    () => bussinesGroup?.details?.[0]?.answers?.answer_text,
    [bussinesGroup?.details],
  )

  const isBusinessAccount = bussinessType === 'Bisnis'
  const sellerVerificationDescription = useMemo(() => {
    const label = !profileData?.is_email_verified
      ? 'email'
      : 'nomor telepon'
    return `Halo ${profileData?.name}, sebelum memulai tahap selanjutnya Anda
        diwajibkan mengisi ${label} terlebih dahulu untuk
        memudahkan proses verifikasi`
  }, [profileData])

  const tracker = eventTracking(trackPosthog())

  //  HANDLER
  const handleNewUserPanelFlow = useCallback(() => {
    if (isLogin) {
      if (isBusinessAccount) {
        setIsOpenModalNIB(true)
      } else {
        setIsOpenModalChangeToBusiness(true)
      }
      tracker.track('Clicked', {
        Name: 'Start Selling Button',
      })
    } else {
      redirect('/login')
    }
  }, [isBusinessAccount, isLogin])

  const handleClickBerjualan = useCallback(() => {
    if (isOnboardingPage) {
      redirect(`${WEB_DESKTOP_RALALI_URL}`)
    } else {
      handleNewUserPanelFlow()
    }
  }, [handleNewUserPanelFlow, isOnboardingPage])

  const goToOnboarding = () => {
    redirect('/seller/onboarding')
  }

  const onSuccessVerify = useCallback(() => {
    getUserProfile().then(() => {
      tracker.track('Submitted', {
        Name: 'Success Verify Account',
      })
      goToOnboarding()
    })
  }, [getUserProfile])

  const handleContinueOpenStore = () => {
    if (isVerified) {
      goToOnboarding()
    } else {
      setIsOpenModalNIB(false)
      setIsOpenVerificationModal(true)
    }

    tracker.track('Clicked', {
      Name: 'Continue Open Store Button',
      'Account Verified': isVerified,
      Action: isVerified ? 'Go to Onboarding' : 'Verify Account',
    })
  }

  const handleCloseModalVerify = () => {
    setIsOpenVerificationModal(false)
    setIsOpenModalNIB(true)
  }

  const handleErrorVerify = () => {
    setIsOpenVerificationModal(false)
    setIsOpenErrorVerifyModal(true)
  }

  const handleCloseErrorVerifModal = () => {
    setIsOpenErrorVerifyModal(false)
    setIsOpenVerificationModal(true)
  }

  const handleOpenModalComparison = () => {
    setIsOpenModalNIB(false)
    setIsOpenModalComparison(true)
    tracker.track('Clicked', {
      Name: 'Learn R-Connect Button',
    })
  }

  // EFFECTS
  useEffect(() => {
    const getInitialData = async () => {
      if (isGetStoreListCalled) return setStoreData(firstStore)

      const [storeRes] = await Promise.all([
        getStoreList(),
        getProfiling(),
      ])

      if (storeRes?.items?.length > 0) {
        return setStoreData(storeRes.items[0])
      }

      return setStoreData(null)
    }

    if (isLogin) {
      getInitialData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    if (version === 'v1') {
      return <Skeleton width="144px" height="40px" />
    }
    return <Skeleton width="144px" height="20px" />
  }

  if (isOnboardingPage) {
    return (
      <StyledButton
        color={buttonColor}
        fontWeight="bold"
        type="button"
        variant={buttonVariant}
        padding="0"
        width="188px"
        onClick={handleClickBerjualan}
      >
        Kunjungi Marketplace
      </StyledButton>
    )
  }

  if (storeData && !isOnboardingPage) {
    return (
      <StoreNameWrapper
        data-testid="store-name-wrapper"
        type={version === 'v1' ? 'bparagraph' : 'rparagraph'}
        onClick={() => redirect('/seller')}
      >
        <Flex gap="8px" alignItems="center">
          <StyledSellerIcon />
          <StoreName>{storeData.name}</StoreName>
        </Flex>
      </StoreNameWrapper>
    )
  }

  if (version === 'v2' && !isLogin) {
    return (
      <StyledButton
        color="black"
        onHoverColor="orange"
        fontWeight="normal"
        variant="text"
        height="auto"
        padding="0"
        onClick={() => redirect('/signup')}
      >
        Jadi Supplier
      </StyledButton>
    )
  }

  return (
    <>
      {version === 'v1' ? (
        <StyledButton
          color={buttonColor}
          fontWeight="bold"
          type="button"
          variant={buttonVariant}
          padding="0"
          width="144px"
          withIcon={{
            icon: buttonIcon,
            position: 'left',
          }}
          onClick={handleClickBerjualan}
          onHoverColor={onHoverColor}
        >
          {buttonText}
        </StyledButton>
      ) : (
        <StyledButton
          color="black"
          onHoverColor="orange"
          fontWeight="normal"
          variant="text"
          height="auto"
          padding="0"
          onClick={handleClickBerjualan}
        >
          {buttonText}
        </StyledButton>
      )}
      {isOpenModalChangeToBusiness && (
        <ModalChangeToBusiness
          onClose={() => setIsOpenModalChangeToBusiness(false)}
          onQuestionnaireSuccess={() => setIsOpenModalNIB(true)}
        />
      )}
      <ModalNIB
        isOpen={isBusinessAccount && isOpenModalNIB}
        onOverlayClick={() => setIsOpenModalNIB(false)}
        onLearnRalaliConnect={handleOpenModalComparison}
        onContinueOpenStore={handleContinueOpenStore}
      />
      <IdentityVerificationModal
        isOpen={isOpenVerificationModal}
        onSuccess={onSuccessVerify}
        onError={handleErrorVerify}
        onClose={handleCloseModalVerify}
        title="Masuk Sebagai Penjual"
        description={sellerVerificationDescription}
        icon={<SellerIllustration />}
      />
      <ModalOtaInvalid
        isOpen={isOpenErrorVerifyModal}
        textButton="Ulangi Proses Verifikasi"
        errMessage={errorMessageOtp}
        onClickBtn={handleCloseErrorVerifModal}
      />
      <ModalRalaliVsConnect
        isOpen={isOpenModalComparison}
        onOverlayClick={() => setIsOpenModalComparison(false)}
      />
    </>
  )
}

export default StartSellingButton
